import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const tomos = [{
  icon: '📗',
  number: 'Volume 1 — Lessons 1 to 30',
  title: 'Classroom Spanish',
  subtitle: 'Greetings · Numbers · Family · Descriptions — the foundation your students need to feel confident in Spanish from day one',
  problem: 'Teaching beginners is one of the most rewarding — and most demanding — parts of being a Spanish teacher. This volume gives you everything you need to guide new students with clarity and structure, without improvising a single activity.',
  bullets: ['How to introduce classroom language and daily routines from the very first lesson', 'How to guide students who have never spoken Spanish before', 'How to build genuine vocabulary retention through repetition and meaningful practice', 'Modifications and differentiation options built into every lesson'],
  result: 'Your beginners feel capable, supported and motivated to keep learning — from their very first class.',
  popular: true
}, {
  icon: '📘',
  number: 'Volume 2 — Lessons 31 to 60',
  title: 'School · Routines · Food · Hobbies',
  subtitle: 'Present-tense verbs — structured, progressive and ready to teach at any pace',
  problem: 'Moving students from basic survival Spanish into real communication is the hardest transition. This volume gives you 30 fully structured lessons that bridge that gap — building vocabulary, routines and the complete present-tense verb system with natural progression.',
  bullets: ['How to build lessons that connect vocabulary to real student life', 'How to introduce regular and irregular present-tense verbs in context', 'How to keep students engaged with genuine variety across six weeks', 'Teaching activities and speaking tasks ready for every lesson'],
  result: 'You teach the middle weeks of Spanish 1 with confidence and your students develop real communicative ability — not just memorized lists.'
}, {
  icon: '📙',
  number: 'Volume 3 — Lessons 61 to 90',
  title: 'Reflexive Verbs · Preterite · Imperfect',
  subtitle: 'Reading and writing — the grammar that separates real Spanish speakers from beginners, completely structured and ready to teach',
  problem: 'Reflexive verbs, the preterite and the imperfect are the concepts students struggle with most — and the ones teachers spend the most time trying to explain from scratch. This volume gives you every lesson, explanation, example and practice activity already built.',
  bullets: ['How to introduce reflexive verbs with clear logic and immediate practice', 'How to teach the preterite tense step by step — regular and irregular forms', 'How to explain the preterite vs. imperfect distinction without confusing your students', 'How to develop intermediate reading and writing skills progressively'],
  result: 'Your students master the most challenging Spanish 1 grammar — and you teach it with a clarity that makes it feel simple.'
}, {
  icon: '📕',
  number: 'Volume 4 — Lessons 91 to 120',
  title: 'Speaking Fluency · Culture · Projects',
  subtitle: 'Advanced writing and final assessment — the complete second half of Spanish 1: oral fluency, cultural knowledge and final mastery',
  problem: "The final stretch of Spanish 1 is where students either consolidate everything they've learned or lose the thread. This volume gives you structured speaking practice, cultural lessons across 20+ Spanish-speaking countries, essay writing, student projects and a comprehensive final assessment — all ready to use.",
  bullets: ['How to build genuine oral fluency through debates, presentations and spontaneous speaking tasks', 'How to teach culture authentically — Mexico, Spain, Colombia, Argentina and beyond', 'How to guide students through five-paragraph essay writing in Spanish', 'How to assess everything fairly using ready-made rubrics and answer keys'],
  result: 'Your students finish Spanish 1 able to speak, read, write and understand Spanish at a level that opens real doors — and you finish the year knowing you delivered a complete, professional curriculum.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-black" data-id="element-306">
      <div className="max-w-6xl mx-auto" data-id="element-307">
        <div className="text-center mb-16" data-id="element-308">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4" data-id="element-309">
            📚 120 Ready-to-Teach Spanish Lessons
          </h2>
          <p className="text-xl text-gold-light" data-id="element-310">
            Spanish 1 and Spanish 2 · 40+ Weeks of Fresh Content · Nothing to
            Build From Scratch
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-311">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-navy-card rounded-2xl border ${tomo.popular ? 'border-gold shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-navy-border'} p-8 relative overflow-hidden flex flex-col h-full`} data-id="element-312">
              {tomo.popular && <div className="absolute top-6 right-[-35px] bg-gold text-black font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-313">
                  MOST POPULAR
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-314">
                <span className="text-4xl" data-id="element-315">{tomo.icon}</span>
                <div data-id="element-316">
                  <div className="text-gold font-bold text-sm uppercase tracking-wider" data-id="element-317">
                    {tomo.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold leading-tight mt-1" data-id="element-318">
                    {tomo.title}
                  </h3>
                </div>
              </div>

              <p className="text-white font-medium mb-6 italic border-l-2 border-gold/50 pl-4" data-id="element-319">
                {tomo.subtitle}
              </p>

              <div className="mb-6" data-id="element-320">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-321">
                  What does it solve in your teaching?
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-322">
                  {tomo.problem}
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-323">
                {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-324">
                    <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" data-id="element-325" />
                    <span className="text-gray-200 text-sm" data-id="element-326">{bullet}</span>
                  </li>)}
              </ul>

              <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 mt-auto" data-id="element-327">
                <span className="text-gold font-bold block mb-1" data-id="element-328">Result:</span>
                <p className="text-white text-sm" data-id="element-329">{tomo.result}</p>
              </div>
            </motion.div>)}
        </div>

        <p className="text-center text-gray-400 text-sm mt-10 max-w-2xl mx-auto" data-id="element-330">
          Every lesson includes: learning objectives · vocabulary focus ·
          grammar focus · teacher guidance · step-by-step instruction · guided
          practice · speaking activity · reading or writing task ·
          differentiation · assessment · answer guidance
        </p>
      </div>
    </section>;
}